import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Building2,
  Users,
  ListOrdered,
  MessageSquareText,
  MessageCircle,
  FileText,
  Settings as SettingsIcon,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/projects", label: "Projects", icon: Building2, testid: "nav-projects" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/stages", label: "Stage Master", icon: ListOrdered, testid: "nav-stages" },
  { to: "/messages", label: "Messages", icon: MessageSquareText, testid: "nav-messages" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "nav-whatsapp" },
  { to: "/summaries", label: "Summaries", icon: FileText, testid: "nav-summaries" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const MOBILE_NAV = NAV.filter((n) =>
  ["/", "/projects", "/summaries", "/messages", "/settings"].includes(n.to)
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-border bg-card fixed inset-y-0 left-0">
        <div className="px-6 py-5 border-b border-border">
          <Brand className="h-14 w-auto" />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-border">
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-xs text-muted-foreground mb-3">
            {user?.role}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-full"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 pb-20 md:pb-0">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-20 backdrop-blur-xl bg-white/80 border-b border-border px-4 py-3 flex items-center justify-between">
          <Brand className="h-9 w-auto" textClassName="font-heading text-base font-bold tracking-tight" />
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button-mobile">
            <LogOut className="h-5 w-5" />
          </Button>
        </header>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 backdrop-blur-xl bg-white/90 border-t border-border flex justify-around">
        {MOBILE_NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`${n.testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-2 px-2 flex-1 text-[10px] font-medium transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <n.icon className="h-5 w-5" />
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
